/**
 * Descarga de paquetes OAIS (SIP/AIP/DIP): el ZIP debe empezar en la carpeta del paquete
 * (p. ej. CAJA_1_...), con metadata/ + data/ + manifest.xml — sin padres CONSULTAS/SIP.
 *
 * Estructura real observada en QA (igual SIP/AIP/DIP):
 *   data/            (carpeta)
 *   metadata/        (carpeta)
 *   manifest.xml     (archivo en la raíz del paquete — no carpeta "manifiesto")
 */

const OAIS_PACKAGE_SEGMENT_NAMES = new Set([
  'metadata',
  'metada',
  'manifiesto',
  'manifest',
  'data',
  'nxti',
  'nxti_info',
]);

/** Segmentos OAIS que deben ir en la descarga completa (mismo comportamiento SIP/AIP/DIP). */
export type OaisPackageSegmentKind = 'metadata' | 'manifiesto' | 'data' | 'nxti';

/** Carpetas hermanas (casing FS Linux / variantes API). */
export const OAIS_FOLDER_PROBE_GROUPS: ReadonlyArray<{
  kind: OaisPackageSegmentKind;
  probes: readonly string[];
}> = [
  { kind: 'metadata', probes: ['metadata', 'Metadata', 'METADATA', 'metada'] },
  {
    kind: 'manifiesto',
    // Carpetas legacy; el caso QA usa manifest.xml en raíz (ver OAIS_ROOT_MANIFEST_FILE_PROBES).
    probes: ['manifiesto', 'Manifiesto', 'manifest', 'Manifest', 'mets', 'METS'],
  },
  { kind: 'data', probes: ['data', 'Data'] },
  { kind: 'nxti', probes: ['nxti', 'nxti_info'] },
];

/**
 * Manifiesto en raíz del paquete (lo que debe aparecer al abrir el ZIP descargado).
 * Prioridad: manifest.xml (estructura SIP/AIP/DIP en QA).
 */
export const OAIS_ROOT_MANIFEST_FILE_PROBES: readonly string[] = [
  'manifest.xml',
  'Manifest.xml',
  'manifiesto.xml',
  'METS.xml',
  'mets.xml',
];

/** Lista plana de sondas de carpeta para recorrido en disco. */
export function allOaisFolderProbeNames(): string[] {
  return OAIS_FOLDER_PROBE_GROUPS.flatMap((group) => [...group.probes]);
}

export function isOaisRootManifestFileName(name: string): boolean {
  const n = (name ?? '').trim().toLowerCase();
  return (
    n === 'manifest.xml' ||
    n === 'manifiesto.xml' ||
    n === 'mets.xml'
  );
}

export function classifyOaisFolderName(name: string): OaisPackageSegmentKind | null {
  const n = (name ?? '').trim().toLowerCase();
  if (!n) {
    return null;
  }
  if (n === 'metadata' || n === 'metada') {
    return 'metadata';
  }
  // Archivo o carpeta de manifiesto
  if (isOaisRootManifestFileName(n) || n.startsWith('manifiest') || n === 'manifest' || n === 'mets') {
    return 'manifiesto';
  }
  if (n === 'data') {
    return 'data';
  }
  if (n === 'nxti' || n === 'nxti_info') {
    return 'nxti';
  }
  return null;
}

/** True si el listado ya muestra metadata o manifiesto (carpeta o manifest.xml). */
export function listingHasOaisMetaOrManifiesto(listedNames: string[]): boolean {
  return listedNames.some((name) => {
    const kind = classifyOaisFolderName(name);
    return kind === 'metadata' || kind === 'manifiesto';
  });
}

/** True si ya hay artifacto de manifiesto (archivo raíz o carpeta). */
export function listingHasOaisManifestArtifact(listedNames: string[]): boolean {
  return listedNames.some((name) => classifyOaisFolderName(name) === 'manifiesto');
}

/**
 * Sondas de carpetas OAIS aún no visibles.
 * Si solo aparece Metadata/data, aún sondea carpeta manifiesto (legacy).
 */
export function resolveMissingOaisSiblingProbes(listedNames: string[]): string[] {
  const present = new Set<OaisPackageSegmentKind>();
  for (const name of listedNames) {
    const kind = classifyOaisFolderName(name);
    if (kind) {
      present.add(kind);
    }
  }
  const probes: string[] = [];
  for (const group of OAIS_FOLDER_PROBE_GROUPS) {
    if (!present.has(group.kind)) {
      probes.push(...group.probes);
    }
  }
  return probes;
}

/**
 * Si falta el manifiesto, candidatos ordenados (solo se usa el PRIMERO que exista).
 * El API puede responder 200 a varias capitalizaciones → no incluir todas.
 */
export function resolveMissingOaisRootManifestFileProbes(listedNames: string[]): string[] {
  if (listingHasOaisManifestArtifact(listedNames)) {
    return [];
  }
  return [...OAIS_ROOT_MANIFEST_FILE_PROBES];
}

/**
 * Un manifiesto por carpeta de paquete (preferencia: manifest.xml).
 * Evita ZIP con manifest.xml + Manifest.xml en el MISMO directorio.
 * No aplasta los manifiestos de varios expedientes bajo SIP.
 */
export function dedupeOaisPackagePaths(paths: string[]): string[] {
  const preferredManifestBase = 'manifest.xml';
  const out: string[] = [];
  const seenKey = new Set<string>();
  const manifestByDir = new Map<string, string>();

  for (const raw of paths) {
    const path = normalizeZipPath(raw);
    if (!path) {
      continue;
    }
    const parts = path.split('/').filter(Boolean);
    const base = parts[parts.length - 1] ?? '';
    if (isOaisRootManifestFileName(base)) {
      const dirKey = parts.slice(0, -1).join('/').toLowerCase();
      const current = manifestByDir.get(dirKey);
      if (!current || base.toLowerCase() === preferredManifestBase) {
        manifestByDir.set(dirKey, path);
      }
      continue;
    }
    const key = path.toLowerCase();
    if (seenKey.has(key)) {
      continue;
    }
    seenKey.add(key);
    out.push(path);
  }

  for (const manifestPath of manifestByDir.values()) {
    out.push(manifestPath);
  }
  return out;
}

/** El listado es la raíz de un paquete OAIS (data/ y/o metadata/). */
export function listingLooksLikeOaisPackageRoot(listedNames: string[]): boolean {
  return listedNames.some((name) => {
    const kind = classifyOaisFolderName(name);
    return kind === 'data' || kind === 'metadata';
  });
}

/** Hay data/metadata pero el API no listó el manifiesto. */
export function shouldProbeOaisRootManifest(listedNames: string[]): boolean {
  return (
    listingLooksLikeOaisPackageRoot(listedNames) &&
    !listingHasOaisManifestArtifact(listedNames)
  );
}

export interface OaisListingFolderHint {
  name?: string | null;
  type?: string | null;
}

function isOaisListingFolderType(type?: string | null): boolean {
  const t = (type ?? '').trim().toLowerCase();
  return t === 'folder' || t === 'directory' || t === 'dir';
}

/**
 * Carpeta SIP/AIP/DIP con varios expedientes (hijos que no son data/metadata/manifiesto).
 */
export function listingLooksLikeOaisPackageContainer(
  listed: OaisListingFolderHint[],
): boolean {
  const names = listed.map((item) => item.name || '');
  if (listingLooksLikeOaisPackageRoot(names)) {
    return false;
  }
  return listed.some(
    (item) =>
      isOaisListingFolderType(item.type) &&
      classifyOaisFolderName(item.name || '') === null &&
      (item.name || '').trim().length > 0,
  );
}

/**
 * Dónde sondear manifest.xml: raíz del paquete, o cada expediente hijo si es contenedor SIP.
 */
export function resolveNestedOaisManifestProbeFolders(
  parentPath: string,
  listed: OaisListingFolderHint[],
): string[] {
  const parent = normalizeZipPath(parentPath).replace(/\/+$/, '');
  const names = listed.map((item) => item.name || '');
  if (listingLooksLikeOaisPackageRoot(names)) {
    return shouldProbeOaisRootManifest(names) ? (parent ? [parent] : []) : [];
  }
  if (!listingLooksLikeOaisPackageContainer(listed)) {
    return [];
  }
  return listed
    .filter(
      (item) =>
        isOaisListingFolderType(item.type) &&
        classifyOaisFolderName(item.name || '') === null &&
        (item.name || '').trim(),
    )
    .map((item) => {
      const name = (item.name || '').trim();
      return parent ? `${parent}/${name}`.replace(/\/+/g, '/') : name;
    });
}

/**
 * Nunca sustituye el listado del API (eso ocultaba las carpetas del SIP).
 * Solo añade filas OAIS extra (metadata/data/manifest) si no estaban.
 */
export function mergeOaisListingItems<T extends { name?: string | null }>(
  listed: T[],
  extra: T[],
): T[] {
  if (extra.length === 0) {
    return listed;
  }
  const byName = new Map<string, T>();
  for (const item of [...listed, ...extra]) {
    const key = (item.name || '').trim().toLowerCase();
    if (!key || byName.has(key)) {
      continue;
    }
    byName.set(key, item);
  }
  return [...byName.values()];
}

export function normalizeZipPath(path: string): string {
  return (path ?? '').trim().replace(/\\/g, '/').replace(/\/+/g, '/');
}

/** Nombre de carpeta del paquete (último segmento de la ruta raíz). */
export function packageFolderNameFromRoot(packageRootPath: string): string {
  const parts = normalizeZipPath(packageRootPath).split('/').filter(Boolean);
  return parts[parts.length - 1] || 'package';
}

/**
 * Si la ruta apunta a Metadata/manifiesto/data/... bajo el paquete,
 * sube a la carpeta raíz del paquete OAIS (CAJA_…).
 */
export function resolveOaisPackageRootPath(path: string): string {
  const normalized = normalizeZipPath(path).replace(/\/+$/, '');
  const hadLeadingSlash = normalized.startsWith('/');
  const parts = normalized.split('/').filter(Boolean);
  while (parts.length > 0) {
    const last = parts[parts.length - 1]?.toLowerCase() ?? '';
    if (!OAIS_PACKAGE_SEGMENT_NAMES.has(last)) {
      break;
    }
    parts.pop();
  }
  const joined = parts.join('/');
  if (!joined) {
    return '';
  }
  return hadLeadingSlash ? `/${joined}` : joined;
}

/**
 * Convierte una ruta absoluta (o entrada de ZIP con padres) a ruta relativa
 * anclada en la carpeta del paquete: `CAJA_1/.../data/file.pdf`.
 */
export function resolvePackageZipEntryPath(
  entryOrFilePath: string,
  packageRootPath: string,
): string {
  const entry = normalizeZipPath(entryOrFilePath).replace(/^\/+/, '');
  const root = normalizeZipPath(packageRootPath).replace(/\/+$/, '');
  const packageName = packageFolderNameFromRoot(root);
  if (!entry) {
    return packageName;
  }

  const entryParts = entry.split('/').filter(Boolean);
  const packageLower = packageName.toLowerCase();
  const idx = entryParts.findIndex((p) => p.toLowerCase() === packageLower);
  if (idx >= 0) {
    return entryParts.slice(idx).join('/');
  }

  const rootTail = root.replace(/^\/+/, '').toLowerCase();
  const entryLower = entry.toLowerCase();
  if (rootTail && entryLower.startsWith(`${rootTail}/`)) {
    const rest = entry.slice(rootTail.length).replace(/^\//, '');
    return rest ? `${packageName}/${rest}` : packageName;
  }
  if (rootTail && entryLower === rootTail) {
    return packageName;
  }

  return `${packageName}/${entryParts.join('/')}`.replace(/\/+/g, '/');
}

/**
 * Une padre + segmento al recorrer un ZIP sin duplicar rutas ni aplanar subcarpetas.
 * Si el API devuelve `data/file.pdf` estando en `data/MiCarpeta`, se conserva el padre
 * y solo se usa el nombre del archivo (mismo criterio que SIP con resolveRepositoryItemPath).
 */
export function joinZipInternalCollectPath(parentInternal: string, segment: string): string {
  const parent = normalizeZipPath(parentInternal)
    .replace(/^\/+/, '')
    .replace(/\/+$/, '');
  const seg = normalizeZipPath(segment)
    .replace(/^\/+/, '')
    .replace(/\/+$/, '');
  if (!seg) {
    return parent;
  }
  if (!parent) {
    return seg;
  }
  const parentLower = parent.toLowerCase();
  const segLower = seg.toLowerCase();
  if (segLower === parentLower || segLower.startsWith(`${parentLower}/`)) {
    return seg;
  }
  // Nombre simple → colgar del padre (preserva MiCarpeta/…).
  if (!seg.includes('/')) {
    return `${parent}/${seg}`.replace(/\/+/g, '/');
  }
  // Ruta “absoluta” del API que no incluye el padre actual → no aplastar la subcarpeta.
  const leaf = seg.split('/').filter(Boolean).pop() ?? seg;
  return `${parent}/${leaf}`.replace(/\/+/g, '/');
}

/**
 * Ruta interna al recolectar del ZIP.
 * Prioriza el path completo del API cuando trae subcarpetas (data/Nueva carpeta/file.pdf);
 * si no, une padre + name (como SIP).
 */
export function resolveZipCollectItemPath(
  item: { name?: string; path?: string; internal_path?: string | null },
  parentInternal: string,
): string {
  const parent = normalizeZipPath(parentInternal)
    .replace(/^\/+/, '')
    .replace(/\/+$/, '');
  const name = normalizeZipPath(item.name ?? '')
    .replace(/^\/+/, '')
    .replace(/\/+$/, '');

  const fromApiRaw = normalizeZipPath(
    String(item.internal_path || item.path || '')
      .replace(/^\/+/, '')
      .replace(/\/+$/, ''),
  );
  // Quitar prefijo filesystem/zip:: si aún viene en path
  let fromApi = fromApiRaw;
  const zipColon = fromApiRaw.toLowerCase().indexOf('.zip::');
  if (zipColon >= 0) {
    fromApi = normalizeZipPath(fromApiRaw.slice(zipColon + '.zip::'.length));
  } else {
    const zipSlash = fromApiRaw.toLowerCase().indexOf('.zip/');
    if (zipSlash >= 0) {
      fromApi = normalizeZipPath(fromApiRaw.slice(zipSlash + '.zip/'.length));
    }
  }

  if (fromApi) {
    if (!parent) {
      return fromApi;
    }
    const parentLower = parent.toLowerCase();
    const apiLower = fromApi.toLowerCase();
    // Path del API bajo el padre → conserva subcarpetas (Nueva carpeta/…).
    if (apiLower === parentLower || apiLower.startsWith(`${parentLower}/`)) {
      return fromApi;
    }
  }

  if (name && !name.includes('/')) {
    return parent ? `${parent}/${name}`.replace(/\/+/g, '/') : name;
  }

  if (fromApi) {
    return joinZipInternalCollectPath(parent, fromApi);
  }
  return parent;
}

/** True si el blob ZIP ya trae layout OAIS completo (data + metadata + manifest). */
export async function zipBlobHasCompleteOaisLayout(zipBlob: Blob): Promise<boolean> {
  try {
    const JSZip = (await import('jszip')).default;
    const zip = await JSZip.loadAsync(zipBlob);
    const names = Object.keys(zip.files).map((n) => n.replace(/\\/g, '/'));
    const hasData = names.some((n) => /(^|\/)data\//i.test(n) || /(^|\/)data$/i.test(n.replace(/\/$/, '')));
    const hasMeta = names.some((n) => /(^|\/)metadata\//i.test(n) || /(^|\/)metadata$/i.test(n.replace(/\/$/, '')));
    const hasManifest = names.some((n) => {
      const base = n.split('/').filter(Boolean).pop() ?? '';
      return isOaisRootManifestFileName(base);
    });
    return hasData && hasMeta && hasManifest;
  } catch {
    return false;
  }
}

/**
 * Mapea `.../DIP|AIP/NOMBRE.zip` → `.../SIP/NOMBRE` (carpeta hermana).
 * Así AIP/DIP pueden descargarse con el mismo flujo que SIP.
 */
export function resolveSipFolderFromOaisZipPath(zipArchivePath: string): string | null {
  const normalized = normalizeZipPath(zipArchivePath).replace(/\/+$/, '');
  const match = normalized.match(/^(.*)\/(DIP|AIP)\/([^/]+)\.zip$/i);
  if (!match) {
    return null;
  }
  const base = match[1] ?? '';
  const packageName = match[3] ?? '';
  if (!base || !packageName) {
    return null;
  }
  return `${base}/SIP/${packageName}`;
}

/**
 * Misma regla SIP/AIP/DIP: ancla al paquete + layout OAIS (data/metadata/manifest).
 */
export function toOaisPackageDownloadEntryPath(
  entryOrInternalPath: string,
  packageRootPath: string,
): string {
  return ensureOaisPackageEntryLayout(
    canonicalizeOaisZipEntryPath(
      resolvePackageZipEntryPath(entryOrInternalPath, packageRootPath),
    ),
  );
}

/** Reescribe entradas del ZIP para que el raíz sea la carpeta del paquete OAIS. */
export async function rebaseZipToPackageRoot(
  zipBlob: Blob,
  packageRootPath: string,
): Promise<Blob> {
  const JSZip = (await import('jszip')).default;
  const source = await JSZip.loadAsync(zipBlob);
  const target = new JSZip();
  const writes: Promise<void>[] = [];
  const usedKeys = new Set<string>();

  source.forEach((relativePath, file) => {
    if (file.dir) {
      return;
    }
    const nextPath = toOaisPackageDownloadEntryPath(relativePath, packageRootPath);
    const key = nextPath.toLowerCase();
    if (usedKeys.has(key)) {
      return;
    }
    usedKeys.add(key);
    writes.push(
      file.async('uint8array').then((data) => {
        target.file(nextPath, data);
      }),
    );
  });

  await Promise.all(writes);
  return target.generateAsync({
    type: 'blob',
    compression: 'DEFLATE',
    compressionOptions: { level: 6 },
  });
}

/**
 * Layout SIP/AIP/DIP: solo data/, metadata/, manifest.xml (o nxti) bajo el paquete.
 * Cualquier otro archivo/carpeta suelta se mueve bajo data/ (mismo orden que SIP).
 */
export function ensureOaisPackageEntryLayout(entryPath: string): string {
  const normalized = normalizeZipPath(entryPath);
  const parts = normalized.split('/').filter(Boolean);
  if (parts.length < 2) {
    return normalized;
  }
  const pkg = parts[0] ?? '';
  const second = (parts[1] ?? '').toLowerCase();
  if (
    second === 'data' ||
    second === 'metadata' ||
    second === 'metada' ||
    second.startsWith('manifiest') ||
    second === 'manifest' ||
    second === 'mets' ||
    second === 'nxti' ||
    second === 'nxti_info' ||
    isOaisRootManifestFileName(parts[1] ?? '')
  ) {
    return parts.join('/');
  }
  // p. ej. PAQUETE/LICENCIA.pdf o PAQUETE/MiCarpeta/x.pdf → bajo data/
  return [pkg, 'data', ...parts.slice(1)].join('/');
}

/**
 * Arma un ZIP OAIS completo a partir de rutas internas del archivo
 * (mismo layout que SIP: package/data|metadata|manifest.xml).
 */
export async function packOaisZipInternalParts(
  parts: Array<{ internalPath: string; blob: Blob }>,
  zipArchivePath: string,
): Promise<Blob> {
  const JSZip = (await import('jszip')).default;
  const target = new JSZip();
  const packageRoot = normalizeZipPath(zipArchivePath).replace(/\.zip$/i, '');
  const usedKeys = new Set<string>();

  // Preferir manifest.xml si hay varias capitalizaciones.
  const ordered = [...parts].sort((a, b) => {
    const aBase = normalizeZipPath(a.internalPath).split('/').pop()?.toLowerCase() ?? '';
    const bBase = normalizeZipPath(b.internalPath).split('/').pop()?.toLowerCase() ?? '';
    if (aBase === 'manifest.xml' && bBase !== 'manifest.xml') {
      return -1;
    }
    if (bBase === 'manifest.xml' && aBase !== 'manifest.xml') {
      return 1;
    }
    return 0;
  });

  await Promise.all(
    ordered.map(async (part) => {
      const internal = normalizeZipPath(part.internalPath).replace(/^\/+/, '');
      if (!internal || !part.blob) {
        return;
      }
      // Misma función que rebaseZipToPackageRoot (descarga SIP).
      const entryPath = toOaisPackageDownloadEntryPath(internal, packageRoot);
      const key = entryPath.toLowerCase();
      if (usedKeys.has(key)) {
        return;
      }
      usedKeys.add(key);
      const data = await part.blob.arrayBuffer();
      target.file(entryPath, data);
    }),
  );

  return target.generateAsync({
    type: 'blob',
    compression: 'DEFLATE',
    compressionOptions: { level: 6 },
  });
}

/** Unifica nombres de manifesto raíz a `manifest.xml` (un solo archivo en el ZIP). */
export function canonicalizeOaisZipEntryPath(entryPath: string): string {
  const normalized = normalizeZipPath(entryPath);
  const parts = normalized.split('/').filter(Boolean);
  if (parts.length === 0) {
    return normalized;
  }
  const last = parts[parts.length - 1] ?? '';
  if (isOaisRootManifestFileName(last)) {
    parts[parts.length - 1] = 'manifest.xml';
  }
  return parts.join('/');
}
